import React from 'react';
import { Page, Text, View, Document, StyleSheet, PDFViewer, PDFDownloadLink, Font, Image } from '@react-pdf/renderer';
import { BrowserView, MobileView } from 'react-device-detect';
import MobilePdfView from 'ui-component/MobilePdfView';
import { Stack, Typography } from '@mui/material';
import logo from 'assets/images/Logo-Aone-Final-Full-1.png';
import moment from 'moment';
import { ToWords } from 'to-words';

Font.register({ family: 'Noto Sans Lao', src: 'https://cdn.jsdelivr.net/gh/googlefonts/noto-fonts@master/hinted/ttf/NotoSansLao/NotoSansLao-Regular.ttf' });

const formatCurrency = (num) => {
    if (num === undefined || num === null || isNaN(num)) return '₭0';
    return `₭${Number(num).toLocaleString('en-US')}`;
};

const toWords = new ToWords({
    localeCode: 'en-US',
    converterOptions: { currency: false, ignoreDecimal: false, ignoreZeroCurrency: false, doNotAddOnly: false }
});

// ─── Styles ──────────────────────────────────────────────────────────────────
const styles = StyleSheet.create({
    body: { paddingTop: 30, paddingBottom: 60, paddingHorizontal: 35, fontFamily: 'Noto Sans Lao' },
    title: { padding: 5, fontSize: 20, textAlign: 'center', fontWeight: 'bold' },
    section: { flexDirection: 'row', alignItems: 'center', fontSize: 9, marginBottom: 2 },
    label: { width: 85, fontWeight: 'bold' },
    value: { flex: 1 },
    logo: { position: 'absolute', top: 20, left: 35, width: 80 },
    table: { width: '100%', marginTop: 10 },
    rowTable: { flexDirection: 'row', fontSize: 9 },
    headerTable: { backgroundColor: '#f8f9fa', fontWeight: 'bold', borderTopWidth: 1, borderTopColor: '#000', borderTopStyle: 'solid' },

    // ── Column widths (UNCHANGED) ────────────────────────────────────────────
    row0: { width: '20%', border: '1px solid #000', borderRight: 'none', paddingTop: 8, paddingBottom: 8, paddingLeft: 4, fontSize: 10, display: 'flex', flexDirection: 'row', flexWrap: 'wrap', overflow: 'hidden' },
    row1: { width: '10%', border: '1px solid #000', borderRight: 'none', paddingTop: 8, paddingBottom: 8, paddingLeft: 4, fontSize: 10, display: 'flex', flexDirection: 'row', flexWrap: 'wrap', overflow: 'hidden' },
    row2: { width: '10%', border: '1px solid #000', borderRight: 'none', paddingTop: 8, paddingBottom: 8, paddingLeft: 4, fontSize: 10, display: 'flex', flexDirection: 'row', flexWrap: 'wrap', overflow: 'hidden' },
    row3: { width: '10%', border: '1px solid #000', borderRight: 'none', paddingTop: 8, paddingBottom: 8, paddingLeft: 4, fontSize: 10, display: 'flex', flexDirection: 'row', flexWrap: 'wrap', overflow: 'hidden' },
    row4: { width: '15%', border: '1px solid #000', paddingTop: 8, paddingBottom: 8, paddingLeft: 4, fontSize: 10, display: 'flex', flexDirection: 'row', flexWrap: 'wrap', overflow: 'hidden' },
    row5: { width: '15%', border: '1px solid #000', paddingTop: 8, paddingBottom: 8, paddingLeft: 4, fontSize: 10, display: 'flex', flexDirection: 'row', flexWrap: 'wrap', overflow: 'hidden' },
    row6: { width: '20%', border: '1px solid #000', paddingTop: 8, paddingBottom: 8, paddingLeft: 4, fontSize: 10, display: 'flex', flexDirection: 'row', flexWrap: 'wrap', overflow: 'hidden' }
});

// ─── Document ─────────────────────────────────────────────────────────────────
const MyDocument = ({ data }) => (
    <Document>
        <Page size="A4" style={styles.body} wrap>
            <Image style={styles.logo} src={logo} />
            <View style={{ paddingTop: 10, paddingLeft: '55%' }}>
                <Text style={{ fontSize: 10, fontWeight: 'bold' }}>{`Announcement No. ${data.announcementNo}`}</Text>
            </View>
            <View style={{ paddingTop: 5, paddingBottom: 10, paddingLeft: '55%' }}>
                <Text style={{ fontSize: 9, fontWeight: 'bold' }}>{`PromotionDate: ${moment(data.startDate).format('DD-MM-yyyy')} - ${moment(data.endDate).format('DD-MM-yyyy')}`}</Text>
            </View>
            <Text style={styles.title}>Announcement Promotion</Text>
            <View style={{ paddingTop: 10, paddingBottom: 10, paddingRight: 10 }} wrap>
                <View style={{ paddingLeft: 5 }}>
                    <View style={styles.section}><Text style={styles.label}>To</Text><Text style={styles.value}>: {data.to}</Text></View>
                    <View style={styles.section}><Text style={styles.label}>Header</Text><Text style={styles.value}>: {data.header}</Text></View>
                    <View style={[styles.section, { paddingTop: 5, paddingBottom: 5 }]} wrap>
                        <Text style={{ fontSize: 9 }}>{`\t\t\t\t\t\t\t  ${data.remark}`}</Text>
                    </View>
                </View>
            </View>
            {/* ── Table (UNCHANGED structure) ── */}
            <View style={styles.table}>
                <View style={[styles.rowTable, { fontWeight: 'bold', backgroundColor: '#f8f9fa', borderTopWidth: 1, borderTopColor: '#000' }]} wrap>
                    <Text style={styles.row0}>Model</Text>
                    <Text style={styles.row1}>ReferencePrice</Text>
                    <Text style={styles.row2}>PomotionPrice</Text>
                    <Text style={styles.row3}>QtyPack</Text>
                    <Text style={styles.row4}>Discount/Unit(₭)</Text>
                    <Text style={styles.row5}>DiscountAMT(₭)</Text>
                    <Text style={styles.row6}>Premiums</Text>
                </View>
                {data.condition.map((row, i) => (
                    <View key={i} style={styles.rowTable} wrap={false}>
                        <Text style={[styles.row0, { fontSize: 8 }]}>{`${row?.model}\n${row?.productName}`}</Text>
                        <Text style={[styles.row1, { fontSize: 8 }]}>{formatCurrency(row?.referencePrice)}</Text>
                        <Text style={[styles.row2, { fontSize: 8 }]}>{formatCurrency(row?.promotionPrice)}</Text>
                        <Text style={[styles.row3, { fontSize: 8 }]}>{row?.qty}</Text>
                        <Text style={[styles.row4, { fontSize: 8 }]}>{formatCurrency(row?.qty > 0 ? row?.discount / row?.qty : 0)}</Text>
                        <Text style={[styles.row5, { fontSize: 8 }]}>{formatCurrency(row?.discount)}</Text>
                        <Text style={[styles.row6, { fontSize: 8 }]}>{`${row?.premiums}`}</Text>
                    </View>
                ))}
            </View>
            {/* ── Signature Section ── */}
            <View style={{ marginTop: 25, flexDirection: 'row', justifyContent: 'space-between' }} wrap={false}>
                <View style={{ width: '48%' }}>
                    <Text style={{ fontSize: 10, fontWeight: 'bold' }}>Verify By: {data.VerifyBy}</Text>
                    <Text style={{ fontSize: 9, marginTop: 10 }}>Signature : .....................................................</Text>
                    <Text style={{ fontSize: 9, marginTop: 5 }}>Date : {moment(data?.VerifyDate).format('DD-MM-yyyy')}</Text>
                </View>
                <View style={{ width: '48%' }}>
                    <Text style={{ fontSize: 10, fontWeight: 'bold' }}>Approve By: {data?.ApproveBy}</Text>
                    <Text style={{ fontSize: 9, marginTop: 10 }}>Signature : .....................................................</Text>
                    <Text style={{ fontSize: 9, marginTop: 5 }}>Date : {moment(data?.ApproveDate).format('DD-MM-yyyy')}</Text>
                </View>
            </View>
            {/* ── Company Info ── */}
            <View style={{ marginTop: 20, borderWidth: 1, borderColor: 'blue', borderRadius: 10, padding: 10 }} wrap={false}>
                <Text style={{ color: 'blue', fontSize: 8, fontWeight: 'bold' }}>A One Distributors Co., Ltd Nongbone Road, Naxay Village, Xaysettha District, Vientiane Lao PDR Mobile: 020 88871111</Text>
            </View>
        </Page>
    </Document>
);

// ─── Component ────────────────────────────────────────────────────────────────
const AnnounncementInvoicePDF = ({ data }) => {
    if (!data) return null;
    return (
        <>
            <BrowserView style={{ width: '100%' }}>
                <PDFViewer style={{ width: '100%', height: '70vh' }}>
                    <MyDocument data={data} />
                </PDFViewer>
            </BrowserView>
            <MobileView style={{ width: '100%' }}>
                <MobilePdfView document={<MyDocument data={data} />} fileName={`Announcement_${data.announcementNo}`} />
            </MobileView>
        </>
    );
};

export default AnnounncementInvoicePDF;
